import React, { useState, useEffect, useContext } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { 
  BookOpen, Search, Filter, Plus, Loader, Edit, 
  Trash2, School, Building, AlertTriangle 
} from 'lucide-react';
import toast from 'react-hot-toast';
import AuthContext from '../../contexts/AuthContext';

/**
 * Admin Courses Page component
 * Manages courses across all schools in the system
 */
const CoursesPage = () => {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  
  const [courses, setCourses] = useState([]);
  const [schools, setSchools] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedSchool, setSelectedSchool] = useState('');
  const [selectedDepartment, setSelectedDepartment] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [deleteConfirmation, setDeleteConfirmation] = useState(null);
  
  useEffect(() => {
    // Redirect if user is not admin
    if (user && user.role !== 'admin') {
      toast.error('You do not have permission to access this page');
      navigate('/school');
      return;
    }
    
    // Fetch data if user is admin
    if (user && user.role === 'admin') {
      fetchData();
    }
  }, [user, navigate]);
  
  const fetchData = async () => {
    try {
      setLoading(true);
      
      // Fetch all schools first
      const schoolsRes = await fetch(
        `${process.env.REACT_APP_API_URL}/api/schools`,
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        }
      );
      
      if (!schoolsRes.ok) {
        throw new Error('Failed to fetch schools');
      }
      
      const schoolsData = await schoolsRes.json();
      setSchools(schoolsData);
      
      // Fetch departments for all schools
      const departmentsPromises = schoolsData.map(school => 
        fetch(`${process.env.REACT_APP_API_URL}/api/schools/${school.id}/departments`, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        }).then(res => res.json())
      );
      
      const departmentsResults = await Promise.all(departmentsPromises);
      // Flatten and combine all departments with their school info
      const allDepartments = departmentsResults.flatMap((depts, i) => 
        depts.map(dept => ({
          ...dept,
          schoolId: schoolsData[i].id,
          schoolName: schoolsData[i].name
        }))
      );
      
      setDepartments(allDepartments);
      
      // Fetch all courses across all schools
      const coursesPromises = schoolsData.map(school => 
        fetch(`${process.env.REACT_APP_API_URL}/api/schools/${school.id}/courses`, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        }).then(res => {
          if (!res.ok) {
            console.error(`Failed to fetch courses for school ${school.id}`);
            return [];
          }
          return res.json();
        })
      );
      
      const coursesResults = await Promise.all(coursesPromises);
      // Flatten and combine all courses with their school info
      const allCourses = coursesResults.flatMap((schoolCourses, i) => 
        schoolCourses.map(course => ({
          ...course,
          schoolId: schoolsData[i].id,
          schoolName: schoolsData[i].name
        }))
      );
      
      setCourses(allCourses);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };
  
  const handleDeleteCourse = async (courseId) => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/courses/${courseId}`,
        {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        }
      );
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to delete course');
      }
      
      // Update courses list
      setCourses(courses.filter(course => course.id !== courseId));
      
      // Close confirmation dialog
      setDeleteConfirmation(null);
      
      toast.success('Course deleted successfully');
    } catch (error) {
      console.error('Error deleting course:', error);
      toast.error(error.message || 'Failed to delete course');
    }
  };
  
  // Filter courses based on search query, school, and department
  const filteredCourses = courses.filter(course => {
    const matchesSearch = 
      course.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      course.code?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (course.description && course.description.toLowerCase().includes(searchQuery.toLowerCase()));
    
    const matchesSchool = selectedSchool ? course.schoolId === selectedSchool : true;
    const matchesDepartment = selectedDepartment ? course.departmentId === selectedDepartment : true;
    
    return matchesSearch && matchesSchool && matchesDepartment;
  });
  
  // Filter departments based on selected school
  const filteredDepartments = selectedSchool 
    ? departments.filter(dept => dept.schoolId === selectedSchool)
    : departments;
  
  return (
    <div>
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
            Course Management
          </h1>
          <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
            Manage all courses across schools
          </p>
        </div>
        
        <Link
            to="/courses/new" 
            state={{ from: 'adminDashboard' }}
            className="mt-4 md:mt-0 inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
            >
            <Plus className="mr-2 -ml-1 h-5 w-5" />
            Add Course
        </Link>
      </div>
      
      {/* Search and Filter */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4 mb-6">
        <div className="relative mb-4">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <Search className="h-5 w-5 text-gray-400" />
          </div>
          <input
            type="text"
            placeholder="Search courses by name, code, or description..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10 pr-4 py-2 w-full border border-gray-300 dark:border-gray-700 rounded-lg focus:ring-red-500 focus:border-red-500"
          />
        </div>
        
        <button
          onClick={() => setShowFilters(!showFilters)}
          className="flex items-center text-gray-600 dark:text-gray-400 hover:text-red-600 dark:hover:text-red-400 transition-colors"
        >
          <Filter className="h-4 w-4 mr-1" />
          <span className="text-sm">{showFilters ? 'Hide filters' : 'Show filters'}</span>
        </button>
        
        {showFilters && (
          <div className="mt-4 p-4 bg-gray-50 dark:bg-gray-700 rounded-lg border border-gray-200 dark:border-gray-600">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label htmlFor="school-filter" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  School
                </label>
                <select
                  id="school-filter"
                  value={selectedSchool}
                  onChange={(e) => {
                    setSelectedSchool(e.target.value);
                    setSelectedDepartment(''); // Reset department filter when school changes
                  }}
                  className="w-full border border-gray-300 dark:border-gray-700 rounded-md py-2 px-3 focus:ring-red-500 focus:border-red-500"
                >
                  <option value="">All Schools</option>
                  {schools.map((school) => (
                    <option key={school.id} value={school.id}>
                      {school.name}
                    </option>
                  ))}
                </select>
              </div>
              
              <div>
                <label htmlFor="department-filter" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Department
                </label>
                <select
                  id="department-filter"
                  value={selectedDepartment}
                  onChange={(e) => setSelectedDepartment(e.target.value)}
                  className="w-full border border-gray-300 dark:border-gray-700 rounded-md py-2 px-3 focus:ring-red-500 focus:border-red-500"
                  disabled={!selectedSchool}
                >
                  <option value="">All Departments</option>
                  {filteredDepartments.map((department) => (
                    <option key={department.id} value={department.id}>
                      {department.code} - {department.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>
        )}
      </div>
      
      {/* Courses Content */}
      {loading ? (
        <div className="flex justify-center items-center py-12">
          <Loader className="h-8 w-8 text-red-500 animate-spin mr-2" />
          <span className="text-gray-600 dark:text-gray-400">Loading courses...</span>
        </div>
      ) : filteredCourses.length === 0 ? (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-8 text-center">
          <BookOpen className="mx-auto h-12 w-12 text-gray-400 dark:text-gray-600" />
          <h3 className="mt-4 text-lg font-medium text-gray-900 dark:text-white">
            No courses found
          </h3>
          <p className="mt-1 text-gray-500 dark:text-gray-400">
            {searchQuery || selectedSchool || selectedDepartment ? 
              'No courses match your current filters.' : 
              'No courses available yet. Get started by adding a course.'}
          </p>
          {(searchQuery || selectedSchool || selectedDepartment) && (
            <button
              onClick={() => {
                setSearchQuery('');
                setSelectedSchool('');
                setSelectedDepartment('');
              }}
              className="mt-4 text-red-600 dark:text-red-400 hover:underline"
            >
              Clear all filters
            </button>
          )}
        </div>
      ) : (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead className="bg-gray-50 dark:bg-gray-900">
                <tr>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    School
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Department
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Code
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Name
                  </th>
                  <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                {filteredCourses.map((course) => (
                  <tr key={course.id} className="hover:bg-gray-50 dark:hover:bg-gray-800">
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                      <div className="flex items-center">
                        <School className="h-4 w-4 mr-2 text-gray-500 dark:text-gray-400" />
                        {course.schoolName}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                      <div className="flex items-center">
                        <Building className="h-4 w-4 mr-2 text-gray-500 dark:text-gray-400" />
                        {course.departmentName || course.departmentCode ? 
                          `${course.departmentName} (${course.departmentCode})` : 
                          'Unknown Department'}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-red-600 dark:text-red-400 font-medium">
                      {course.code || 'N/A'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                      <Link to={`/courses/${course.id}`} className="hover:underline">
                        {course.name || 'Unnamed Course'}
                      </Link>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <Link
                        to={`/courses/${course.id}/edit`}
                        className="text-blue-600 hover:text-blue-900 dark:text-blue-400 dark:hover:text-blue-300 mr-4"
                      >
                        <Edit className="h-4 w-4 inline mr-1" />
                        Edit
                      </Link>
                      <button
                        onClick={() => setDeleteConfirmation(course)}
                        className="text-red-600 hover:text-red-900 dark:text-red-400 dark:hover:text-red-300"
                      >
                        <Trash2 className="h-4 w-4 inline mr-1" />
                        Delete
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
      
      {/* Delete Confirmation Modal */}
      {deleteConfirmation && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-md w-full">
            <div className="flex items-center text-red-600 dark:text-red-400 mb-4">
              <AlertTriangle className="h-6 w-6 mr-2" />
              <h3 className="text-lg font-medium">Confirm Deletion</h3>
            </div>
            <p className="text-gray-600 dark:text-gray-400 mb-6">
              Are you sure you want to delete the course <strong>{deleteConfirmation.code}: {deleteConfirmation.name}</strong>? This action cannot be undone and will remove all associated data.
            </p>
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => setDeleteConfirmation(null)}
                className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"
              >
                Cancel
              </button>
              <button
                onClick={() => handleDeleteCourse(deleteConfirmation.id)}
                className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CoursesPage;